import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { tokens } from '../../theme';
import PublicIcon from '@mui/icons-material/Public';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Header from '../../components/Header';
import ConfirmDeleteDialog from '../../components/ConfirmDeleteDialog';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { can, filterActionColumns } from '../../utils/permissions';

const COORDINATORS_API = `${process.env.REACT_APP_APILINK}/admin/coordinator-expenses/coordinators`;

const AllCountryExcursions = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [openToast, setOpenToast] = useState(false);
  const [toastMessage, setToastmessage] = useState('');

  const token = localStorage.getItem('token');
  const adminUserJson = localStorage.getItem('adminUser');
  const adminUser = adminUserJson ? JSON.parse(adminUserJson) : null;
  const canAssignCoordinator = Number(adminUser?.role) === 1 || Number(adminUser?.role) === 2;

  const [allCountries, setallCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [idToDelete, setIdToDelete] = useState(null);

  const [coordinators, setCoordinators] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignRow, setAssignRow] = useState(null);
  const [selectedCoordinatorId, setSelectedCoordinatorId] = useState('');
  const [assignSubmitting, setAssignSubmitting] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [departureDate, setDepartureDate] = useState('');
  const [sortModel, setSortModel] = useState([{ field: 'DepartureDate', sort: 'desc' }]);

  // Debounce the search box so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const getData = useCallback(
    async (signal) => {
      setLoading(true);
      try {
        const sortField = sortModel[0]?.field || 'DepartureDate';
        const sortOrder = sortModel[0]?.sort || 'desc';
        const res = await fetch(
          `${
            process.env.REACT_APP_APILINK
          }/admin/country-excursion?page=${page}&limit=${pageSize}&sortBy=${sortField}&sortOrder=${sortOrder}&search=${encodeURIComponent(
            search
          )}&departureDate=${encodeURIComponent(departureDate)}`,
          { headers: { token }, signal }
        );
        const data = await res.json();
        if (data.success) {
          setallCountries(data.data || []);
          setTotalCount(data.pagination?.total || 0);
        }
      } catch (e) {
        if (e.name === 'AbortError') return;
        console.error('AllCountryExcursions fetch error', e);
      }
      if (!signal?.aborted) setLoading(false);
    },
    [token, page, pageSize, search, departureDate, sortModel]
  );

  const fetchCoordinators = useCallback(async () => {
    try {
      const res = await fetch(COORDINATORS_API, { headers: { token } });
      const data = await res.json();
      if (data.success) setCoordinators(data.data || []);
    } catch (e) {
      console.error('Failed to load coordinators', e);
    }
  }, [token]);

  useEffect(() => {
    const controller = new AbortController();
    getData(controller.signal);
    return () => controller.abort();
  }, [getData]);

  useEffect(() => {
    fetchCoordinators();
  }, [fetchCoordinators]);

  const handleClearSearch = () => {
    setSearchInput('');
    setSearch('');
    setDepartureDate('');
    setPage(1);
  };

  const handleDepartureDateChange = (e) => {
    setDepartureDate(e.target.value);
    setPage(1);
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
    setPage(1);
  };

  // Delete destination excursion
  const handleDelete = async (id) => {
    const res = await fetch(`${process.env.REACT_APP_APILINK}/admin/country-excursion/${id}`, {
      headers: { token },
      method: 'DELETE',
    });
    const data = await res.json();
    if (data.message) {
      setOpenToast(true);
      setToastmessage(data.message);
      if (data.success) {
        getData();
      }
    }
  };

  const openDeleteDialog = (id) => {
    setIdToDelete(id);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setIdToDelete(null);
  };

  const confirmDelete = async () => {
    if (idToDelete == null) return;
    await handleDelete(idToDelete);
    closeDeleteDialog();
  };

  const openAssignModal = useCallback((row) => {
    setAssignRow(row);
    setSelectedCoordinatorId(row.coordinatorId != null ? String(row.coordinatorId) : '');
    setAssignOpen(true);
  }, []);

  const closeAssignModal = useCallback(() => {
    setAssignOpen(false);
    setAssignRow(null);
    setSelectedCoordinatorId('');
  }, []);

  const handleAssignSave = async () => {
    if (!assignRow) return;
    setAssignSubmitting(true);
    try {
      const coordinatorId =
        selectedCoordinatorId === '' ? null : Number(selectedCoordinatorId);
      const res = await fetch(
        `${process.env.REACT_APP_APILINK}/admin/country-excursion/${encodeURIComponent(assignRow.id)}/coordinator`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', token },
          body: JSON.stringify({ coordinatorId }),
        }
      );
      const data = await res.json();
      setOpenToast(true);
      setToastmessage(data.message || (data.success ? 'Saved' : 'Failed'));
      if (data.success) {
        closeAssignModal();
        getData();
      }
    } catch (e) {
      setOpenToast(true);
      setToastmessage('Failed to assign coordinator');
    } finally {
      setAssignSubmitting(false);
    }
  };

  const columns = useMemo(
    () => [
    {
      field: 'CountryImage',
      headerName: 'Image',
      filterable: false,
      renderCell: (cellValues) => {
        return (
          <img
            src={cellValues.row.CountryImage}
            style={{ height: '100%', padding: '5px', aspectRatio: '16/9', objectFit: 'cover' }}
            alt="Image"
          />
        );
      },
    },
    {
      field: 'CountryName',
      headerName: 'Destination Name',
      flex: 1,
      minWidth: 150,
      cellClassName: 'name-column--cell',
    },
    {
      field: 'DepartureDate',
      headerName: 'Departure Date',
      flex: 1,
      minWidth: 150,
      cellClassName: 'name-column--cell',
    },
    {
      field: 'CountryDescription',
      headerName: 'Description',
      flex: 1,
      minWidth: 150,
      cellClassName: 'name-column--cell',
    },
    {
      field: 'numberOfExcursions',
      headerName: 'No. of Excursions',
      minWidth: 100,
      flex: 1,
      cellClassName: 'name-column--cell',
    },
    {
      field: 'pdf',
      headerName: 'Schedule PDF',
      minWidth: 120,
      sortable: false,
      renderCell: (cellValues) => {
        return cellValues.row.pdf ? (
          <a href={cellValues.row.pdf} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
            <Button
              sx={{
                backgroundColor: colors.blueAccent[600],
                color: colors.grey[100],
                minWidth: '42px',
                width: '42px',
                height: '42px',
                padding: '0',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  backgroundColor: colors.blueAccent[700],
                  transform: 'scale(1.1)',
                },
              }}
              variant="contained"
            >
              <PictureAsPdfIcon sx={{ fontSize: '25px' }} />
            </Button>
          </a>
        ) : (
          <i>-</i>
        );
      },
    },
    {
      field: 'coordinatorUsername',
      headerName: 'Coordinator',
      flex: 1,
      minWidth: 140,
      renderCell: (cellValues) => (cellValues.row.coordinatorUsername ? cellValues.row.coordinatorUsername : <i>-</i>),
    },
    {
      field: 'assignCoordinator',
      headerName: 'Assign',
      filterable: false,
      sortable: false,
      minWidth: 110,
      renderCell: (cellValues) =>
        canAssignCoordinator ? (
          <Button
            sx={{
              backgroundColor: colors.blueAccent[600],
              color: colors.grey[100],
              ':hover': { backgroundColor: colors.blueAccent[700] },
              fontSize: '13px',
              textTransform: 'none',
            }}
            variant="contained"
            onClick={() => openAssignModal(cellValues.row)}
          >
            Assign
          </Button>
        ) : (
          <i>-</i>
        ),
    },
    {
      field: 'Edit',
      filterable: false,
      renderCell: (cellValues) => {
        return (
          <Link to={`/country-excursion/${cellValues.row.id}`}>
            <Button
              sx={{
                backgroundColor: colors.greenAccent[600],
                color: colors.grey[100],
                ':hover': { backgroundColor: colors.greenAccent[700] },
              }}
              variant="contained"
            >
              <EditIcon />
            </Button>
          </Link>
        );
      },
    },
    {
      field: 'Duplicate',
      filterable: false,
      sortable: false,
      renderCell: (cellValues) => {
        return (
          <Link to={`/country-excursion/${cellValues.row.id}/duplicate`}>
            <Button
              sx={{
                backgroundColor: colors.grey[500],
                color: colors.grey[100],
                ':hover': { backgroundColor: colors.grey[600] },
              }}
              variant="contained"
            >
              <ContentCopyIcon />
            </Button>
          </Link>
        );
      },
    },
    {
      field: 'Delete',
      filterable: false,
      renderCell: (cellValues) => {
        return (
          <Button
            sx={{
              backgroundColor: colors.redAccent[500],
              color: colors.grey[100],
              ':hover': { backgroundColor: colors.redAccent[600] },
            }}
            variant="contained"
            onClick={() => openDeleteDialog(cellValues.row.id)}
          >
            <DeleteIcon />
          </Button>
        );
      },
    },
    ],
    [canAssignCoordinator, colors, openAssignModal]
  );

  // Toast
  const handleCloseToast = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenToast(false);
  };

  const action = (
    <>
      <IconButton size="small" aria-label="close" color="inherit" onClick={handleCloseToast}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </>
  );

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" gap="10px" flexWrap="wrap">
        <Header title="DESTINATION EXCURSIONS" subtitle="All Travel Destination Excursions" />

        {can('trips.create') && (
          <Link to={'/country-excursion/add'}>
            <Button
              sx={{
                backgroundColor: colors.blueAccent[700],
                color: colors.grey[100],
                fontSize: '14px',
                fontWeight: 'bold',
                padding: '10px 20px',
                '&:hover': {
                  backgroundColor: colors.blueAccent[800],
                },
              }}
            >
              <PublicIcon sx={{ mr: '10px' }} />
              Add Destination Excursions
            </Button>
          </Link>
        )}
      </Box>

      <Box display="flex" gap="10px" flexWrap="wrap" alignItems="center" m="10px 0 0 0">
        <TextField
          size="small"
          placeholder="Search by destination name, description..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: colors.grey[300] }} />,
          }}
          sx={{ minWidth: '320px', backgroundColor: colors.primary[400] }}
        />
        <TextField
          type="date"
          size="small"
          label="Departure Date"
          value={departureDate}
          onChange={handleDepartureDateChange}
          InputLabelProps={{ shrink: true, sx: { color: colors.grey[300] } }}
          sx={{
            minWidth: '180px',
            backgroundColor: colors.primary[400],
            '& input::-webkit-calendar-picker-indicator': { filter: 'invert(1)' },
          }}
        />
      </Box>

      {(search || departureDate) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1, mb: 1 }}>
          <Typography variant="body2" sx={{ color: colors.grey[300], fontWeight: 500 }}>
            Active filters:
          </Typography>
          {search && (
            <Chip
              label={`Search: ${search}`}
              size="small"
              onDelete={() => {
                setSearchInput('');
                setSearch('');
                setPage(1);
              }}
              sx={{ backgroundColor: colors.blueAccent[700], color: colors.grey[100], '& .MuiChip-deleteIcon': { color: colors.grey[300] } }}
            />
          )}
          {departureDate && (
            <Chip
              label={`Departure Date: ${departureDate}`}
              size="small"
              onDelete={() => {
                setDepartureDate('');
                setPage(1);
              }}
              sx={{ backgroundColor: colors.blueAccent[700], color: colors.grey[100], '& .MuiChip-deleteIcon': { color: colors.grey[300] } }}
            />
          )}
          {!loading && (
            <Typography variant="body2" sx={{ color: colors.greenAccent[400], ml: 1 }}>
              {totalCount} result{totalCount !== 1 ? 's' : ''}
            </Typography>
          )}
          <Typography
            variant="body2"
            onClick={handleClearSearch}
            sx={{
              color: colors.redAccent[500],
              cursor: 'pointer',
              ml: 1,
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Clear all
          </Typography>
        </Box>
      )}

      <Box
        m="10px 0 0 0"
        height="667px"
        sx={{
          '& .MuiDataGrid-root': {
            border: 'none',
          },
          '& .MuiDataGrid-cell': {
            borderBottom: 'none',
          },
          '& .name-column--cell': {
            color: colors.grey[100],
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: colors.blueAccent[700],
            borderBottom: 'none',
          },
          '& .MuiDataGrid-virtualScroller': {
            backgroundColor: colors.primary[400],
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: 'none',
            backgroundColor: colors.blueAccent[700],
          },
          '& .MuiCheckbox-root': {
            color: `${colors.greenAccent[200]} !important`,
          },
          '& .MuiDataGrid-toolbarContainer .MuiButton-text': {
            color: `${colors.grey[100]} !important`,
            fontSize: 14,
          },
        }}
      >
        <DataGrid
          rows={allCountries}
          columns={filterActionColumns(columns, {
            Edit: 'trips.edit',
            Duplicate: 'trips.create',
            Delete: 'trips.delete',
          })}
          getRowId={(row) => row.id}
          disableColumnFilter
          loading={loading}
          paginationMode="server"
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          page={page - 1}
          pageSize={pageSize}
          rowCount={totalCount}
          rowsPerPageOptions={[10, 25, 50]}
          onPageChange={(p) => setPage(p + 1)}
          onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
        />
      </Box>

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        onConfirm={confirmDelete}
        title="Delete destination excursion?"
        message="Are you sure you want to delete this destination excursion?"
        colors={colors}
      />

      <Dialog
        open={assignOpen}
        onClose={(_, reason) => {
          if (assignSubmitting) return;
          closeAssignModal();
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 12px 35px rgba(0,0,0,0.35)',
          },
        }}
      >
        <DialogTitle
          sx={{
            py: 2,
            borderBottom: `1px solid ${colors.primary[600]}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box display="flex" alignItems="center" gap={1.25}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: colors.blueAccent[700],
              }}
            >
              <PersonOutlineIcon sx={{ fontSize: 19 }} />
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ color: colors.grey[100], fontWeight: 700, lineHeight: 1.2 }}>
                Assign Coordinator
              </Typography>
              <Typography variant="caption" sx={{ color: colors.grey[300] }}>
                Destination excursion responsibility
              </Typography>
            </Box>
          </Box>
          <IconButton
            size="small"
            disabled={assignSubmitting}
            onClick={closeAssignModal}
            sx={{ color: colors.grey[200] }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 2.5, pb: 2 }}>
          {assignRow && (
            <>
              <Box
                sx={{
                  mb: 2,
                  px: 2,
                  py: 1.5,
                  borderRadius: '12px',
                  border: `1px solid ${colors.blueAccent[800]}`,
                  backgroundColor: colors.primary[500],
                }}
              >
                <Typography variant="caption" sx={{ color: colors.grey[300], display: 'block', mb: 0.5 }}>
                  Destination & Departure Date
                </Typography>
                <Typography variant="body1" sx={{ color: colors.grey[100], fontWeight: 700 }}>
                  {assignRow.CountryName}
                </Typography>
                <Chip
                  size="small"
                  label={`Departure: ${assignRow.DepartureDate}`}
                  sx={{
                    mt: 1,
                    bgcolor: colors.blueAccent[700],
                    color: colors.grey[100],
                    fontWeight: 600,
                    borderRadius: '8px',
                  }}
                />
              </Box>

              <FormControl fullWidth sx={{ mt: 1 }}>
                <InputLabel id="assign-coordinator-label">Coordinator</InputLabel>
                <Select
                  labelId="assign-coordinator-label"
                  label="Coordinator"
                  value={selectedCoordinatorId}
                  onChange={(e) => setSelectedCoordinatorId(e.target.value)}
                  sx={{
                    borderRadius: '10px',
                    '& .MuiSelect-select': {
                      py: 1.55,
                    },
                  }}
                >
                  <MenuItem value="">
                    <em>Unassigned</em>
                  </MenuItem>
                  {coordinators.map((c) => (
                    <MenuItem key={c.id} value={String(c.id)}>
                      {c.username}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" sx={{ mt: 1.5, color: colors.grey[300], display: 'block' }}>
                Choose <em>Unassigned</em> if you want to remove current coordinator assignment.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 2.5,
            pt: 2,
            borderTop: `1px solid ${colors.primary[600]}`,
            gap: 1.25,
          }}
        >
          <Button
            onClick={closeAssignModal}
            disabled={assignSubmitting}
            sx={{
              color: colors.grey[100],
              borderColor: colors.grey[500],
              minWidth: '100px',
              borderRadius: '9px',
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleAssignSave}
            disabled={assignSubmitting}
            variant="contained"
            sx={{
              backgroundColor: colors.greenAccent[600],
              '&:hover': { backgroundColor: colors.greenAccent[700] },
              minWidth: '120px',
              borderRadius: '9px',
              fontWeight: 700,
            }}
          >
            {assignSubmitting ? <CircularProgress size={22} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={openToast}
        autoHideDuration={5000}
        onClose={handleCloseToast}
        message={toastMessage}
        action={action}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </Box>
  );
};

export default AllCountryExcursions;
